// @safetyhub/report-composer — 작성폼 파생 (가시성 모델 P1)
//
// ReportTemplate의 노드 트리를 순회해 **모든 바인딩**을 수집하고, 각각에 대응하는
// InputWidget(kind:'input' — 작성 위젯 트리의 입력 노드)을 만든다.
//
// ⚠️ 2026-09-30(S2) → 2026-10-02(S6), 결정 `2026-09-30-writeform-to-composer-widget-tree-consolidation`:
//   S2에서 파생 결과 타입이 flat `WriteFormNode[]` → `InputWidget[]`로 바뀌었고(어댑터
//   `inputWidgetFromNode` 제거 — 그 어댑터가 전파하던 집합이 이 파생이 실제로 채우는 12필드를
//   전부 덮고 있어 동작 중립이었다), **S6에서 flat 축 자체가 철거됐다** —
//   `WriteFormNode`·`ReportTemplate.writeForm`이 삭제되고 그 선언·doc은 `InputWidget`이 흡수했다.
//
// 📏 **그 12필드를 세는 법**(숫자를 인용하기 전에 다시 세라 — 이 숫자는 2026-10-02에 「13」으로
//   적혀 있었고 `composerWidgets.ts`가 그것을 어댑터 삭제의 근거로 인용했다. 실제는 12였다):
//   `const node: InputWidget = {` 블록 셋(scaffoldFromBinding · variables · dataDefs)의 리터럴 키와
//   `node.<키> =` 재대입을 합집합으로 센다. `packages/report-composer`에서:
//     { awk '/const node: InputWidget = \{/{b=1} b; /^ *\};$/{b=0}' src/writeForm.ts \
//         | grep -oE '^ *(\.\.\.\([^{]*\{ )?[a-zA-Z]+:' | grep -oE '[a-zA-Z]+:'; \
//       grep -oE 'node\.[a-zA-Z]+ =' src/writeForm.ts | sed -E 's/node\.([a-zA-Z]+) =/\1:/'; } \
//       | sort -u
//   2026-10-05 실측 12: kind·id·bindingKey·widget·label·dataType·options·optionsFrom·
//   defaultValue·prefillFrom·required·fields. ⚠️ 개수만 보지 말고 **이름 목록**을 보라 —
//   하나가 빠지고 하나가 들어온 교환은 개수로 드러나지 않는다.
//   ⚠️ 이것은 `InputWidget` **선언** 필드 수가 아니다(선언은 훨씬 많다 — labelStyle·format·
//   collapseLines 등 저작 전용 축은 이 파생이 채우지 않고 저작 트리가 소유한다).
//
// ⭐ 그래서 **이 파생은 골격(스캐폴드)만 낸다 — 저장된 override를 더 이상 병합하지 않는다.**
//   작성 폼의 저작 정본은 `composerWidgetTree`이고, 소비처는 트리가 있으면 그것을 쓴다
//   (web `new-composed.tsx`: `writeTree = effectiveTree ?? nodes`). 즉 이 파생은 **트리가 없는
//   템플릿의 폴백**이다.
//
// 출처(input/data/computed/system/const) 무관하게
// 작성폼에 노출하되 mode(editable/readonly/hidden)로 표시 정책을 구분한다.
//   - mode 기본 editable(미지정=editable로 취급, 저장 절약 + 하위호환).
//   - hidden 노드도 수집(mode='hidden') — 작성 페이지 제외는 web(P3), 구성 뷰 dimmed는 admin(P2) 담당.
//
// 위젯 추론:
//   - input 카탈로그 바인딩: entry 메타(widget/label/validation/options/defaultValue)로 풍부 스캐폴드.
//   - 그 외(data/computed/ai) 바인딩: 카탈로그 returns에서 select 경로 끝 필드의 type을 매핑
//     (number→number, string→text, enum→select(+value-set options), array→table(+항목 필드), date→date),
//     추론 불가/필드 없음 → text 폴백.
//   - 레거시 source==='input' 또는 catalog 미발견: text 폴백.
//
// 결정 기록: docs/decisions/2026-06-19-report-composer-table-model-and-binding.md §6
//            docs/decisions/2026-06-19-report-composer-catalog-driven-authoring.md (C3)

import type {
  Binding,
  CatalogEntry,
  CatalogField,
  CatalogIndex,
  ComposerNode,
  InputOption,
  InputWidget,
  ReportTemplate,
  TableNode,
  TemplateVariable,
  WidgetType,
} from '@safetyhub/shared';
import { VALUE_SETS } from '@safetyhub/shared';
import { walkBindings, type BindingOwner } from './walkBindings';

/** 카탈로그 entry.returns에서 select 경로에 해당하는 필드를 찾는다. */
function findField(returns: CatalogField[], select: string): CatalogField | undefined {
  // 1) 평면 직접 매칭 — 'workplaceName', 'site.address'(점 포함 리터럴 path) 등.
  const direct = returns.find((f) => f.path === select);
  if (direct) return direct;

  // 2) 중첩/배열 인덱스 경로 강하(예: 'findings.0.description', 'aiSummary.summary').
  let fields = returns;
  let matched: CatalogField | undefined;
  for (const seg of select.split('.')) {
    if (/^\d+$/.test(seg)) continue; // 배열 인덱스 — 항목 스키마 불변
    const f = fields.find((x) => x.path === seg);
    if (!f) return undefined;
    matched = f;
    fields = f.fields ?? [];
  }
  return matched;
}

/**
 * input 카탈로그 entry.widget에서 바인딩 데이터 구조 type(dataType)을 역추론한다.
 * 작성폼 탭에서 위젯 후보를 거르는 데 쓴다(input은 widget이 고정이지만 호환 위젯으로만 제한).
 * 특수 위젯(photo/aiTrigger/time/통계)은 매핑 없음(undefined=제약 없음).
 */
function dataTypeForInputWidget(widget: WidgetType): InputWidget['dataType'] {
  switch (widget) {
    // combobox 값=string(입력/선택 텍스트) — text와 동일 구조 type(2026-07-16 P1).
    case 'text':
    case 'textarea':
    case 'combobox':
      return 'string';
    case 'number':
      return 'number';
    case 'select':
    case 'radio':
      return 'enum';
    case 'checkbox':
      return 'boolean';
    case 'date':
      return 'date';
    case 'table':
      return 'array';
    default:
      // photo/aiTrigger/time 등 → 제약 없음.
      // date-range도 **의도적으로** 여기(default)로 떨어진다 — 누락이 아니다. dataType은 카탈로그
      // input entry의 값 구조를 역추론하는 축인데 기간 객체({start,end})에 대응하는 dataType이
      // 없고, 애초에 date-range는 카탈로그 input이 아니라 행 필드 선언에서만 생성된다.
      return undefined;
  }
}

/** data/computed/ai entry의 returns에서 select 경로 필드 type을 작성 위젯으로 추론한다. */
function inferWidget(
  entry: CatalogEntry,
  select?: string,
): {
  widget: WidgetType;
  label?: string;
  options?: InputOption[];
  fields?: InputWidget['fields'];
  dataType?: InputWidget['dataType'];
} {
  if (!select) return { widget: 'text' };
  const field = findField(entry.returns, select);
  if (!field) return { widget: 'text' };

  switch (field.type) {
    case 'number':
      return { widget: 'number', label: field.label, dataType: 'number' };
    case 'enum':
      return {
        widget: 'select',
        label: field.label,
        options: [...VALUE_SETS[field.valueSetId]],
        dataType: 'enum',
      };
    case 'array':
      return {
        widget: 'table',
        label: field.label,
        fields: (field.fields ?? []).map((f) => ({ key: f.path, label: f.label, type: f.type })),
        dataType: 'array',
      };
    case 'date':
      return { widget: 'date', label: field.label, dataType: 'date' };
    case 'string':
      return { widget: 'text', label: field.label, dataType: 'string' };
    default:
      // boolean/object 등 → text 폴백 + 제약 없음(dataType 미부여, widget=text와 정합).
      return { widget: 'text', label: field.label };
  }
}

/** 단일 바인딩에서 작성폼 노드를 스캐폴드한다(mode 미부여 = editable 기본). */
function scaffoldFromBinding(binding: Binding, catalog?: CatalogIndex): InputWidget {
  const node: InputWidget = {
    kind: 'input',
    id: `wf-${binding.bindingKey}`,
    bindingKey: binding.bindingKey,
    widget: 'text',
    // 라벨 폴백 **정본**: catalog[catalogKey].label → binding.label(양식 원문) → bindingKey.
    // 카탈로그가 앞인 이유는 🔗 데이터 연결이 «의미를 확정한 행위»라 양식 문구보다 정확해서다.
    // binding.label이 중간에 있는 근거는 그 JSDoc(2026-10-04 P3 — 전체 재구성이 트리를 body에서
    // 새로 만들 때 라벨 보유자가 트리 하나뿐이면 라벨을 잃는다).
    label: binding.label ?? binding.bindingKey,
  };

  if (binding.catalogKey) {
    const entry = catalog?.[binding.catalogKey];
    if (entry?.kind === 'input') {
      node.widget = entry.widget;
      node.label = entry.label ?? binding.label ?? binding.bindingKey;
      if (entry.validation?.required) node.required = true;
      if (entry.options) node.options = entry.options;
      if (entry.defaultValue !== undefined) node.defaultValue = entry.defaultValue;
      const dt = dataTypeForInputWidget(entry.widget);
      if (dt) node.dataType = dt;
    } else if (entry) {
      const inferred = inferWidget(entry, binding.select);
      node.widget = inferred.widget;
      node.label = inferred.label ?? entry.label ?? binding.bindingKey;
      if (inferred.options) node.options = inferred.options;
      if (inferred.fields) node.fields = inferred.fields;
      if (inferred.dataType) node.dataType = inferred.dataType;
    }
    // entry 미발견 → text 폴백(위 기본값 유지).
  }
  // 레거시 source(input/system/ai/const) 또는 catalogKey 부재 → text 폴백.

  if (binding.prefillFrom) node.prefillFrom = binding.prefillFrom;
  return node;
}

/**
 * template.body를 재귀 순회해 tableId→TableNode 맵을 만든다.
 * deriveWriteForm이 반복영역(repeatRegion) 밴드 셀 바인딩을 flat 파생에서 걸러낼 때 쓴다.
 * 순회 범위는 walkBindings/findNodeById와 동형(group/stack.children · repeat.template ·
 * branch 슬롯 · 표 셀 content).
 */
function collectTables(nodes: ComposerNode[], map: Map<string, TableNode>): void {
  for (const node of nodes) {
    switch (node.kind) {
      case 'table':
        map.set(node.id, node);
        for (const cell of node.cells) {
          if (cell.content) collectTables(cell.content, map);
        }
        break;
      case 'group':
      case 'stack':
        collectTables(node.children, map);
        break;
      case 'repeat':
        collectTables([node.template], map);
        break;
      case 'branch':
        for (const slot of node.branches) collectTables(slot.children, map);
        break;
      default:
        break;
    }
  }
}

/**
 * (row, col) 셀이 그 표의 반복영역(repeatRegion) 밴드 안에 있는지.
 * 반복 행 셀은 행 그리드(RowsCollectionEditor)로 편집하므로 flat 작성폼 파생 대상이 아니다.
 * colStart/colEnd 기본값은 전개 규약(RenderTemplate)과 동일: colStart??0, colEnd??colCount-1.
 * 헤더/정적 셀(밴드 밖)은 통과 → 회귀 0.
 */
function isCellInRepeatRegion(table: TableNode, row: number, col: number): boolean {
  for (const r of table.repeatRegions ?? []) {
    const colStart = r.colStart ?? 0;
    const colEnd = r.colEnd ?? table.colCount - 1;
    if (row >= r.rowStart && row <= r.rowEnd && col >= colStart && col <= colEnd) return true;
  }
  return false;
}

/** 노드 + 그 하위(group/stack children · table 셀 content · 중첩 repeat/branch)의 모든 id를 set에 담는다. */
function collectSubtreeIds(node: ComposerNode, set: Set<string>): void {
  set.add(node.id);
  switch (node.kind) {
    case 'group':
    case 'stack':
      for (const c of node.children) collectSubtreeIds(c, set);
      break;
    case 'table':
      for (const cell of node.cells) {
        if (cell.content) for (const c of cell.content) collectSubtreeIds(c, set);
      }
      break;
    case 'repeat':
      collectSubtreeIds(node.template, set);
      break;
    case 'branch':
      for (const slot of node.branches) for (const c of slot.children) collectSubtreeIds(c, set);
      break;
    default:
      break;
  }
}

/**
 * 모든 repeat 노드의 template 서브트리에 속한 노드 id를 수집한다.
 * repeat 노드는 over 배열을 순회하며 template을 반복 렌더하므로, template 안 바인딩은 반복 행 데이터라
 * (표 repeatRegion 밴드 셀과 마찬가지로) flat 작성폼 파생 대상이 아니다 — 편집형이면 행 그리드,
 * readonly면 표시 전용. 반환 set으로 deriveWriteForm이 그 노드/셀 바인딩을 flat 파생에서 제외한다.
 */
function collectRepeatSubtreeIds(nodes: ComposerNode[], set: Set<string>): void {
  for (const node of nodes) {
    switch (node.kind) {
      case 'repeat':
        collectSubtreeIds(node.template, set);
        break;
      case 'group':
      case 'stack':
        collectRepeatSubtreeIds(node.children, set);
        break;
      case 'table':
        for (const cell of node.cells) {
          if (cell.content) collectRepeatSubtreeIds(cell.content, set);
        }
        break;
      case 'branch':
        for (const slot of node.branches) collectRepeatSubtreeIds(slot.children, set);
        break;
      default:
        break;
    }
  }
}

/**
 * 반복영역(repeatRegion) 밴드 셀의 **content 안** 노드 id를 모두 수집한다.
 *
 * 반복 셀 자체 바인딩(source:'cell')은 isCellInRepeatRegion으로 제외되지만, 셀 content로 배치된
 * 노드(예: 선택지 그룹 choiceGroup, 손저작 체크박스)의 바인딩은 source:'node'라 그 제외를 타지 않는다.
 * 이 노드들의 데이터는 반복 행 필드라 행 그리드(dataDef 표 위젯)로 편집되므로, flat 작성폼 노드로
 * 파생하면 안 된다(RowsCollectionEditor와 이중 노출·고아 필드). 여기 수집된 id의 node 바인딩을 제외한다.
 */
function collectRepeatRegionContentIds(nodes: ComposerNode[], set: Set<string>): void {
  for (const node of nodes) {
    switch (node.kind) {
      case 'table':
        for (const cell of node.cells) {
          if (!cell.content) continue;
          if (isCellInRepeatRegion(node, cell.row, cell.col)) {
            for (const c of cell.content) collectSubtreeIds(c, set);
          } else {
            // 반복영역 밖 셀의 content 안에 중첩 표가 있을 수 있으므로 계속 하강.
            collectRepeatRegionContentIds(cell.content, set);
          }
        }
        break;
      case 'group':
      case 'stack':
        collectRepeatRegionContentIds(node.children, set);
        break;
      case 'repeat':
        collectRepeatRegionContentIds([node.template], set);
        break;
      case 'branch':
        for (const slot of node.branches) collectRepeatRegionContentIds(slot.children, set);
        break;
      default:
        break;
    }
  }
}

/**
 * body에서 tableId→TableNode 인덱스를 만든다(collectTables의 공개 래퍼).
 *
 * walkBindings의 cell/computeInput owner는 `cell.tableId`만 주므로, 소유 표 노드가 필요한 소비처
 * (deriveWriteForm의 repeatRegion 판정, admin 위젯 클립보드의 셀 바인딩→표 노드 역인덱스)가
 * 이 인덱스를 공유한다.
 */
export function buildTableIndex(nodes: ComposerNode[]): Map<string, TableNode> {
  const map = new Map<string, TableNode>();
  collectTables(nodes, map);
  return map;
}

/**
 * **작성폼 파생 대상 바인딩 판정기(정본)** — deriveWriteForm의 제외 규칙을 한 벌로 모은 것.
 *
 * body를 한 번 스캔해 제외 인덱스(표 맵·repeat 서브트리 id·repeatRegion 셀 content id)를 만들고,
 * `walkBindings`가 준 owner가 **작성폼 위젯으로 파생되는 바인딩**인지 판정하는 술어를 돌려준다.
 * 제외 대상(=false):
 *   - `computeInput`(계산 입력) / `conditionBinding`(운영자 조건 데이터)
 *   - 표 반복영역(repeatRegion) 밴드 안 셀 바인딩 — 행 그리드로 편집
 *   - repeat 노드 template 서브트리 안 바인딩(node/cell 모두) — 반복 행 데이터
 *   - repeatRegion 밴드 셀 content 노드의 바인딩(선택지 그룹·손저작 체크박스 등)
 *
 * ⚠️ 이 규칙을 복제하지 말 것. "어떤 body 바인딩이 작성 위젯을 만드는가"를 알아야 하는 소비처
 *    (admin 위젯 클립보드의 파생 위젯 → 소유 body 노드 역인덱스 등)는 반드시 이 술어를 재사용한다 —
 *    평행 순회를 손으로 짜면 규칙이 갈라져 엉뚱한 노드를 파생 소유자로 오인한다.
 */
export function createWriteFormBindingFilter(
  body: ComposerNode[],
): (owner: BindingOwner) => boolean {
  const tableMap = buildTableIndex(body);
  // repeat 노드 template 서브트리 노드 id — 그 안 바인딩은 반복 행 데이터라 flat 파생 제외.
  const repeatSubtreeIds = new Set<string>();
  collectRepeatSubtreeIds(body, repeatSubtreeIds);
  // 표 반복영역(repeatRegion) 밴드 셀의 content 노드 id — 반복 행 필드라 flat 파생 제외
  // (선택지 그룹·손저작 체크박스 등이 dataDef 표 위젯과 이중 노출되지 않게).
  const repeatRegionContentIds = new Set<string>();
  collectRepeatRegionContentIds(body, repeatRegionContentIds);

  return (owner: BindingOwner): boolean => {
    // compute 입력·branch 조건 데이터 바인딩은 작성폼 위젯 파생 대상이 아니다(운영자 조건 데이터/계산 입력).
    if (owner.source === 'computeInput' || owner.source === 'conditionBinding') return false;
    // 반복영역(repeatRegion) 밴드 안 셀 바인딩은 행 그리드(RowsCollectionEditor)로 편집하므로
    // flat 작성폼 노드로 파생하지 않는다(위임 작성폼 이중 노출 방지). 헤더/정적 셀은 통과.
    if (owner.source === 'cell' && owner.cell) {
      const table = tableMap.get(owner.cell.tableId);
      if (table && isCellInRepeatRegion(table, owner.cell.row, owner.cell.col)) return false;
      if (repeatSubtreeIds.has(owner.cell.tableId)) return false;
    }
    if (owner.source === 'node' && owner.nodeId) {
      // repeat 노드 template 서브트리 안 바인딩도 반복 행 데이터라 flat 파생 제외.
      if (repeatSubtreeIds.has(owner.nodeId)) return false;
      // 표 반복영역 밴드 셀의 content 노드 바인딩(선택지 그룹 등)도 반복 행 필드라 제외.
      if (repeatRegionContentIds.has(owner.nodeId)) return false;
    }
    return true;
  };
}

/** 전역 변수 위젯 → dataType(작성폼 위젯 후보 필터 근거, dataTypeForInputWidget와 정합). */
function variableDataType(widget: TemplateVariable['widget']): InputWidget['dataType'] {
  switch (widget) {
    case 'number':
      return 'number';
    case 'select':
      return 'enum';
    case 'checkbox':
      return 'boolean';
    case 'text':
    default:
      return 'string';
  }
}

/**
 * 템플릿의 **모든 바인딩**에서 작성폼 노드를 파생한다(가시성 모델 P1).
 *
 * - 출처 무관 전 바인딩 수집 + mode 기본 editable(미부여) + 출처별 위젯 추론.
 * - 트리 순서 유지, bindingKey 중복 제거(첫 등장만). 바인딩 없는 정적 노드는 제외.
 * - template.variables(전역 변수)는 작성폼 **최상단**에 파생(전역 설정값). 참조는 content[key] top-level 저장.
 * - dataDefs(N2-A)는 def별 widget:'table' 작성노드를 input 파생 뒤에 append.
 *
 * ⚠️ **저장된 override를 병합하지 않는다**(2026-10-02 S6). 종전에는 `template.writeForm`(flat
 * override 채널)을 `applyOverride`로 덮었는데 그 축 자체가 철거됐다 — 작성 폼의 저작 정본은
 * `composerWidgetTree`이고 이 파생은 **골격(스캐폴드)만** 낸다. 저작 트리가 있으면 **렌더·검증**은
 * 그것을 쓴다(`writeTree = 저작 트리 ?? 이 파생`). 그 규약은 작성 surface 셋에 다 있지만 형태가
 * 둘로 갈린다(web 인증 작성·admin 시뮬은 `?? `, web 외부 위임은 `&& length > 0`) — 「세 곳이 같은
 * 코드」로 인용하지 말 것.
 *
 * ⚠️ **그러나 이 파생은 렌더 밖에서도 쓰인다** — 초기값 산출 세 자리(web 인증 작성·web 외부
 * 위임 `deriveInitialValues`·admin `buildInitialWriteValues`)와 admin 속성창(`writeFormFields`).
 * 즉 「트리가 있으면 파생이 안 쓰인다」가 아니라 **「렌더에만 안 쓰인다」**다. override 철거가
 * 그쪽에 미친 영향은 0으로 쟀고, 근거는 admin `simulationLogic.ts` `buildInitialWriteValues`
 * doc이 정본이다(갈리는 축은 `widget === 'table'` 하나 · form-import는 `table`을 낼 수 없다).
 *
 * ⭐ 사라진 `applyOverride`가 품고 있던 **label 비대칭**도 함께 기록해 둔다: 그 함수는
 * `node.label = override.label`로 **조건 없이** 덮어 키가 없는 행이 오면 파생 라벨을 `undefined`로
 * 지웠고(admin `mergeWriteForm`은 `?? `로 파생을 유지했다), 그 차이가 발현하지 않은 이유는 코드가
 * 같아서가 아니라 **flat 484노드가 `label` 키를 전수 보유했기 때문**이다. 철거로 그 잠재 결함이
 * 소멸했으니 「두 병합이 같은 일을 했다」로 일반화하지 말 것.
 *
 * catalog는 optional — 없으면 카탈로그 기반 바인딩은 text 폴백(레거시 source는 동작 불변).
 */
export function deriveWriteForm(template: ReportTemplate, catalog?: CatalogIndex): InputWidget[] {
  const seen = new Set<string>();
  const result: InputWidget[] = [];

  // 양식 전역 변수(Template Variables) — 작성폼 **최상단** 필드로 파생(렌더 바인딩 파생과 별개 경로).
  // 전역 설정값 성격이라 노드 바인딩보다 앞에 노출. 값은 content[key](top-level)에 저장되고
  // RenderTemplate.mergeRepeatScope가 모든 노드/반복 행 scope에 병합 → 어느 노드든 key로 참조(플러밍 무변경).
  // 노드 바인딩/dataDef가 같은 key를 이미 차지하면(아래 seen) 건너뛴다 — 전역 변수 선언이 우선.
  for (const v of template.variables ?? []) {
    if (seen.has(v.key)) continue;
    seen.add(v.key);
    const node: InputWidget = {
      kind: 'input',
      id: `wf-${v.key}`,
      bindingKey: v.key,
      widget: v.widget,
      label: v.label,
      dataType: variableDataType(v.widget),
      ...(v.options ? { options: v.options } : {}),
      // select 옵션 동적 소스(개정 4) — 렌더가 카탈로그 rows(resolvedRows/values 채널)에서 옵션을
      // 동적 생성. rows 미해소면 위 정적 options 폴백(external 등 무크래시).
      ...(v.optionsFrom ? { optionsFrom: v.optionsFrom } : {}),
      ...(v.default !== undefined ? { defaultValue: v.default } : {}),
      // 서버 resolve 기본값 공급(대안 A) — 클라 인지용 전파(서버 prefill은 template.variables 직접 수집).
      ...(v.prefillFrom ? { prefillFrom: v.prefillFrom } : {}),
    };
    result.push(node);
  }

  // 작성 입력 대상 바인딩만 수집한다(트리 순서 유지). compute 입력 바인딩(source==='computeInput')은
  // RenderData 값 공유는 실재하나 작성폼 위젯 파생 대상이 아니므로 제외 → 종전 collectBindings와 동일 산출.
  // checkbox 노드(checkedValue 미지정)의 바인딩은 boolean 입력 위젯(checkbox)으로 파생한다.
  // 같은 bindingKey를 공유하는 checkbox 노드들이 각기 다른 checkedValue(non-empty)를 가지면
  // 단일선택 라디오 그룹(예 [v]예/[ ]아니오)이라 select 입력으로 자동 파생한다(옵션=각 체크박스 {checkedValue,label}).
  // 제외 규칙 정본(createWriteFormBindingFilter) — admin 위젯 클립보드 역인덱스가 같은 술어를
  // 재사용한다(규칙 두 벌 금지).
  const isWriteFormBinding = createWriteFormBindingFilter(template.body);

  // bindingKey → 라디오 옵션(트리 순서 보존, checkedValue 중복 dedup). select 파생에 쓴다.
  const radioOptionsByKey = new Map<string, InputOption[]>();
  const bindings: { binding: Binding; deriveCheckbox: boolean }[] = [];
  walkBindings(template.body, (binding, owner) => {
    if (!isWriteFormBinding(owner)) return;
    const owningNode = owner.node;
    // checkedValue 미지정 또는 ''(둘 다 render에서 truthy 판정 boolean)은 boolean checkbox로 파생.
    const deriveCheckbox =
      owningNode?.kind === 'checkbox' &&
      (owningNode.checkedValue === undefined || owningNode.checkedValue === '');
    // 라디오식 checkbox(checkedValue non-empty)는 그 bindingKey의 select 옵션으로 수집한다.
    // '' 또는 미지정은 truthy 판정 boolean checkbox라 옵션에서 제외(deriveCheckbox 경로 유지).
    if (owningNode?.kind === 'checkbox') {
      const cv = owningNode.checkedValue;
      if (cv !== undefined && cv !== '') {
        const opts = radioOptionsByKey.get(binding.bindingKey) ?? [];
        // 같은 checkedValue 중복 dedup, 트리 순서 보존.
        if (!opts.some((o) => o.value === cv)) {
          opts.push({ value: cv, label: owningNode.label ?? cv });
        }
        radioOptionsByKey.set(binding.bindingKey, opts);
      }
    }
    bindings.push({ binding, deriveCheckbox });
  });

  for (const { binding, deriveCheckbox } of bindings) {
    if (seen.has(binding.bindingKey)) continue;
    seen.add(binding.bindingKey);
    const node = scaffoldFromBinding(binding, catalog);
    const radioOptions = radioOptionsByKey.get(binding.bindingKey);
    if (radioOptions && radioOptions.length > 0) {
      // 라디오 그룹(공유키 checkbox) → 단일선택 radio 위젯(작성폼이 라디오 버튼으로 렌더).
      // scaffold label/속성은 유지. 옵션 많으면 운영자가 override로 select 전환 가능.
      node.widget = 'radio';
      node.options = radioOptions;
      node.dataType = 'string';
    } else if (deriveCheckbox) {
      node.widget = 'checkbox';
      node.dataType = 'boolean';
    }
    result.push(node);
  }

  // dataDefs(N2-A) → 각 def마다 편집 가능한 표 작성노드 append (input 파생 뒤).
  // bindingKey 중복 방지(input 바인딩이 같은 key를 이미 차지하면 skip).
  for (const def of template.dataDefs ?? []) {
    // compute(동기화 데이터)는 원본 목록에서 렌더마다 재조립되는 파생이라 작성폼 직접 편집 대상이
    // 아니다 — 표 작성노드로 파생하지 않는다(2026-07-26 접두 자동 노출 개편. 작성 탭이 def.key를
    // []로 초기화해 미리보기 조인이 비던 사고 계열도 원천 차단).
    if (def.compute) continue;
    if (seen.has(def.key)) continue;
    seen.add(def.key);
    const node: InputWidget = {
      kind: 'input',
      id: `wf-${def.key}`,
      bindingKey: def.key,
      widget: 'table',
      label: def.label,
      fields: def.fields,
      dataType: 'array',
      // N2-D: 리스트 prefill — def.prefillFrom을 표 작성노드에 전파(클라 인지용).
      ...(def.prefillFrom ? { prefillFrom: def.prefillFrom } : {}),
    };
    result.push(node);
  }
  return result;
}
